import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AnimatePresence, motion } from 'motion/react'
import { X } from 'lucide-react'
import {
    AdviceChatAdvisor,
    AdviceChatMessage,
    TriggeredAdviceLatest,
    dismissTriggeredAdvice,
    getAdviceChat,
    getProjectTriggeredAdviceLatest,
} from '../../api/client'

// Stage 6: slim notification card surfaced above the Activity Calendar.
// Shows the latest non-dismissed triggered advice for the project, with a
// one-line preview pulled from the first `ass:advisor_message` of the chat;
// expanding shows the first 2-3 advisor messages, and "Continue in chat →"
// navigates to /advice/:chatId so the user can drop into the live thread.
//
// The plan offered two ways to source the preview; we go with the second
// (fetch the full advice chat via `getAdviceChat`, find the first advisor
// row client-side) because the endpoint already exists, returns author_id,
// and Stage 5 leans on it for the chat view — no new backend surface.

interface Props {
    projectId: number
}

const PREVIEW_CHARS = 120
const EXPANDED_MAX_MESSAGES = 3

const advisorDisplayName = (advisor?: AdviceChatAdvisor): string => {
    if (!advisor) return 'Advisor'
    const nickname = advisor.nickname || ''
    const first = advisor.first_name || ''
    const last = advisor.last_name || ''
    const full = `${first} ${last}`.trim()
    if (nickname) return nickname
    if (full) return full
    if (advisor.archetype) return advisor.archetype
    return 'Advisor'
}

const truncate = (text: string, n: number): string => {
    if (text.length <= n) return text
    return text.slice(0, n).trimEnd() + '…'
}

const TriggeredAdviceCard: React.FC<Props> = ({ projectId }) => {
    const [latest, setLatest] = useState<TriggeredAdviceLatest | null>(null)
    const [advisorMessages, setAdvisorMessages] = useState<AdviceChatMessage[]>([])
    const [advisorsById, setAdvisorsById] = useState<Record<number, AdviceChatAdvisor>>({})
    const [expanded, setExpanded] = useState(false)
    const [dismissing, setDismissing] = useState(false)
    const [hidden, setHidden] = useState(false)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        let cancelled = false
        const load = async () => {
            setLoading(true)
            try {
                const row = await getProjectTriggeredAdviceLatest(projectId)
                if (cancelled) return
                if (!row || row.dismissed_at) {
                    setLatest(null)
                    return
                }
                setLatest(row)
                // Pull the chat to find the first advisor message for the
                // preview. We accept the cost — one extra GET per project
                // page load only when there's an active advice trigger.
                try {
                    const chat = await getAdviceChat(row.chat_id)
                    if (cancelled) return
                    const byId: Record<number, AdviceChatAdvisor> = {}
                    for (const a of chat.chat.advisors || []) {
                        if (typeof a.id === 'number') byId[a.id] = a
                    }
                    setAdvisorsById(byId)
                    const advisorRows = (chat.messages || []).filter(
                        (m) =>
                            m.role === 'assistant' &&
                            m.message_type === 'ass:advisor_message',
                    )
                    setAdvisorMessages(advisorRows)
                } catch (e) {
                    // Preview is best-effort; on failure the card still
                    // shows trigger metadata + Continue link.
                    console.error('Failed to load advice chat for preview:', e)
                }
            } catch (e) {
                console.error('Failed to load triggered advice:', e)
            } finally {
                if (!cancelled) setLoading(false)
            }
        }
        load()
        return () => {
            cancelled = true
        }
    }, [projectId])

    const firstMessage = advisorMessages[0]
    const firstAdvisor = useMemo(() => {
        if (!firstMessage || firstMessage.author_id == null) return undefined
        return advisorsById[firstMessage.author_id]
    }, [firstMessage, advisorsById])

    const previewText = useMemo(() => {
        if (!firstMessage) return ''
        return truncate(firstMessage.content || '', PREVIEW_CHARS)
    }, [firstMessage])

    const handleDismiss = async () => {
        if (!latest || dismissing) return
        setDismissing(true)
        try {
            await dismissTriggeredAdvice(projectId, latest.id)
            setHidden(true)
        } catch (e) {
            console.error('Failed to dismiss triggered advice:', e)
            setDismissing(false)
        }
    }

    if (loading || hidden || !latest) return null

    const advisorName = advisorDisplayName(firstAdvisor)
    const avatarUrl =
        firstAdvisor?.profile_picture_url || '/static/images/default_avatar.jpg'

    // Display the first EXPANDED_MAX_MESSAGES advisor messages on expand.
    const expandedMessages = advisorMessages.slice(0, EXPANDED_MAX_MESSAGES)

    return (
        <div
            className="bg-white p-4 sm:rounded-lg sm:shadow-md mb-8 border-l-4 border-amber-500"
            data-testid="triggered-advice-card"
        >
            <div className="flex items-start gap-3">
                <img
                    src={avatarUrl}
                    alt={`${advisorName} avatar`}
                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                />
                <div className="flex-grow min-w-0">
                    <div className="flex items-center justify-between mb-1">
                        <div className="text-sm font-semibold text-amber-900">
                            {advisorName}
                            {latest.chat_title && (
                                <span className="text-gray-500 font-normal">
                                    {' '}
                                    — {latest.chat_title}
                                </span>
                            )}
                        </div>
                        <motion.button
                            onClick={handleDismiss}
                            disabled={dismissing}
                            whileHover={{ scale: 1.1, rotate: 90 }}
                            whileTap={{ scale: 0.9 }}
                            transition={{
                                type: 'spring',
                                stiffness: 400,
                                damping: 20,
                            }}
                            className="ml-2 text-gray-400 hover:text-gray-700 flex-shrink-0"
                            aria-label="Dismiss advice"
                            data-testid="triggered-advice-dismiss"
                        >
                            <X size={18} />
                        </motion.button>
                    </div>

                    {previewText ? (
                        <p
                            className="text-gray-700 text-sm"
                            data-testid="triggered-advice-preview"
                        >
                            {previewText}
                        </p>
                    ) : (
                        <p className="text-gray-500 text-sm italic">
                            New advice from your squad is ready.
                        </p>
                    )}

                    <AnimatePresence initial={false}>
                        {expanded && expandedMessages.length > 0 && (
                            <motion.div
                                key="expanded"
                                initial={{ opacity: 0, scale: 0.95, y: -8 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95, y: -8 }}
                                transition={{
                                    type: 'spring',
                                    stiffness: 500,
                                    damping: 30,
                                }}
                                style={{ transformOrigin: 'top left' }}
                                className="mt-3 space-y-3"
                                data-testid="triggered-advice-expanded"
                            >
                                {expandedMessages.map((m) => {
                                    const advisor =
                                        m.author_id != null
                                            ? advisorsById[m.author_id]
                                            : undefined
                                    const name = advisorDisplayName(advisor)
                                    return (
                                        <div
                                            key={m.id}
                                            className="border-l-2 border-gray-200 pl-3"
                                        >
                                            <div className="text-xs font-semibold text-amber-900 mb-1">
                                                {name}
                                            </div>
                                            <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                                {m.content}
                                            </p>
                                        </div>
                                    )
                                })}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <div className="flex items-center gap-4 mt-3 text-sm">
                        {advisorMessages.length > 0 && (
                            <button
                                onClick={() => setExpanded((v) => !v)}
                                className="text-amber-800 hover:text-amber-900 font-medium"
                                data-testid="triggered-advice-toggle"
                            >
                                {expanded ? 'Show less' : 'Read more'}
                            </button>
                        )}
                        <Link
                            to={`/advice/${latest.chat_id}`}
                            className="text-amber-800 hover:text-amber-900 font-medium"
                            data-testid="triggered-advice-continue"
                        >
                            Continue in chat →
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default TriggeredAdviceCard
